import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import useMsalB2C, { MSAL_STORAGE_KEYS } from "hooks/useMsalB2C";
import { setToken } from "hooks/useSetToken";
import Loader from "components/blocks/Loader/Loader";

type LoginState =
    | { status: "loading" | "redirecting" | "success" }
    | { status: "error"; message: string };

const Internal: React.FC = () => {
    const navigate = useNavigate();
    const { search } = useLocation();
    const { internalLoginRequest } = useMsalB2C();
    const [state, setState] = useState<LoginState>({ status: "loading" });

    useEffect(() => {
        let active = true;
        let finished = false;
        setState({ status: "loading" });
        // Bound this page's wait, not the external B2C page or the underlying MSAL operation.
        const timeout = window.setTimeout(() => {
            if (!active || finished) return;

            finished = true;
            const message = "Se ha agotado el tiempo de espera para iniciar la sesion. No se ha reintentado el acceso.";
            console.error("Error en el inicio de sesion por impersonacion:", message);
            setState({ status: "error", message });
        }, 60_000);

        internalLoginRequest(search)
            .then((result) => {
                // A late result must not overwrite the timeout or navigate after unmount.
                if (!active || finished) return;
                if (!result) {
                    setState({ status: "redirecting" });
                    return;
                }

                setToken(result.idToken);
                // Remove legacy polling flags without clearing unrelated application data.
                Object.values(MSAL_STORAGE_KEYS).forEach((key) => localStorage.removeItem(key));
                navigate("/inicio", { replace: true });
                finished = true;
                window.clearTimeout(timeout);
                setState({ status: "success" });
            })
            .catch((cause: unknown) => {
                if (!active || finished) return;

                finished = true;
                window.clearTimeout(timeout);
                console.error("Error en el inicio de sesion por impersonacion:", cause);
                setState({
                    status: "error",
                    message: cause instanceof Error && cause.message.trim()
                        ? cause.message
                        : "No se ha podido iniciar la sesion por impersonacion.",
                });
            });

        return () => {
            active = false;
            window.clearTimeout(timeout);
        };
    }, [internalLoginRequest, search, navigate]);

    return (
        <>
            <div>Internal login</div>
            <Loader
                activeLoader={state.status === "loading" || state.status === "redirecting"}
                label={state.status === "redirecting" ? "Redirigiendo a B2C..." : "Iniciando sesion..."}
            />
            {state.status === "error" && <div role="alert">{state.message}</div>}
            {state.status === "success" && <div role="status">Sesion iniciada. Accediendo al portal...</div>}
        </>
    );
};

export default Internal;
